import axiosClient from "@src/utils/axios-client";
import Config from "@src/utils/api/config";

export type SubscriptionItem = {
  id: number;
  status: string; // "ACTIVE" | "CANCELED"
  startedAt: string;
  canceledAt: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: number;
    displayName: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    palmsId?: string;
  };
  canceledBy?: {
    id: number;
    displayName: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    userType?: { id: number; name: string };
  };
  subscription: {
    id: string;
    name: string;
    price: number;
    rewards: number;
    palmsPoints?: number;
    subscriptionPeriod: number;
    periodUnit: 'Weekly VIP' | 'Monthly VIP';
    goldBonus: number;
    isActive: number;
    allGamesAccess: boolean;
    vipGamesAccess: boolean;
    earnGoldWithSilverPlay: boolean;
    vipDrawAccess: boolean;
    tournamentAccess: boolean;
  };
};

export type SubscriptionAPIResponse = {
  items: SubscriptionItem[];
  totalRecords: number;
};

export type AllSubscriptionParams = {
  sort?: string;
  startDate?: string;
  endDate?: string;
  offset?: number;
  pageNumber?: number;
  pageSize?: number;
  search?: string | number;
  status?: string;
  subscriptionId?: string;
};

export type SubscriptionOverviewParams = {
  startDate?: string;
  endDate?: string;
  subscriptionId?: string;
};

export const allSubscriptionsAPI = async (params: AllSubscriptionParams): Promise<SubscriptionAPIResponse> => {
  try {
    const res = await axiosClient.get('/user-subscriptions', { params: {
      partnerId: Config.PARTNER_ID,
      ...params,
    } });
    return res.data || { items: [], totalRecords: 0 };
  } catch (error: any) {
    throw error?.response?.data?.error || error?.message || 'Failed to fetch subscriptions';
  }
};

export const subscriptionOverview = async (params: SubscriptionOverviewParams) => {
  try {
    const res = await axiosClient.get('/subscription/overview', { params: {
      partnerId: Config.PARTNER_ID,
      ...params,
    } });
    return res.data || {};
  } catch (error: any) {
    throw error?.response?.data?.error || error?.message || 'Failed to fetch stats';
  }
};

export const subscriptionPurchaseCounts = async () => {
  try {
    const res = await axiosClient.get('/subscription/purchases/counts', { params: {
      partnerId: Config.PARTNER_ID,
    }});
    return res.data || {};
  } catch (error: any) {
    throw error?.response?.data?.error || error?.message || 'Failed to fetch stats';
  }
};

export const getSubscriptionAPI = async (id: number): Promise<SubscriptionItem> => {
  try {
    const res = await axiosClient.get(`/user-subscriptions/${id}`);
    return res.data;
  } catch (error: any) {
    throw error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to fetch subscription details';
  }
};

export const cancelSubscriptionAPI = async (id: number) => {
  try {
    const res = await axiosClient.post(`/user-subscriptions/${id}/cancel`);
    return res.data;
  } catch (error: any) {
    throw error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to cancel subscription';
  }
};

export type SubscriptionTransaction = {
  id: number;
  transactionNo: string;
  user: {
    id: number;
    displayName: string;
  };
  subscription: {
    id: string;
    name: string;
    periodUnit: 'Weekly VIP' | 'Monthly VIP';
    price: number;
  };
  purchaseItemName: string;
  paymentMethod: string;
  status: string; // "COMPLETED" | "FAILED" | "PENDING"
  transactionDate: string;
  amount: number;
  paymentTypeID: number;
  action: string;
};

export type SubscriptionTransactionsResponse = {
  items: SubscriptionTransaction[];
  totalRecords: number;
};

export const getSubscriptionTransactionsAPI = async (params: {
  reference_id: number;
  purchase_item_type: string;
}): Promise<SubscriptionTransactionsResponse> => {
  try {
    const requestParams = {
      page: 1,
      size: 100,
      sort: "transactionDate,desc",
      partnerId: Config.PARTNER_ID,
      ...params,
    };

    const purchases = await axiosClient.post(
      "/payment/report/getPayment",
      requestParams
    );

    return purchases.data.data || {};
  } catch (error: any) {
    throw error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to fetch transactions';
  }
};

export const exportSubscriptionsAPI = async (params: any) => {
  try {
    const res = await axiosClient.get('/subscriptions/export', { params: {
      partnerId: Config.PARTNER_ID,
      ...params,
    } });
    return res.data || [];
  } catch (error: any) {
    throw error?.response?.data?.error || error?.message || 'Failed to export';
  }
};